import React, { useState } from 'react';
import {
    AppBar, Toolbar, Button, IconButton, Box,
    Drawer, List, ListItem, ListItemText, Divider, useMediaQuery, Collapse
} from '@mui/material';
import {
    Menu as MenuIcon,
    Close as CloseIcon,
    AccountCircle as AccountCircleIcon,
    Search as SearchIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import SearchBar from './SearchBar';

type AppMenuProps = {
    onLoginClick?: () => void;
    onSignupClick?: () => void;
    currentPath?: string;
};

const SearchBarWrapper: React.FC = () => (
    <Box sx={{ backgroundColor: 'transparent', py: 0 }}>
        <SearchBar />
    </Box>
);

const AppMenu: React.FC<AppMenuProps> = ({ onLoginClick, onSignupClick, currentPath = '' }) => {
    const { logout, isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

    const [profileDrawerOpen, setProfileDrawerOpen] = useState(false);
    const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
    const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

    const handleProfileMenuOpen = () => {
        setProfileDrawerOpen(true);
    };

    const handleProfileMenuClose = () => {
        setProfileDrawerOpen(false);
    };

    const handleLogout = async () => {
        try {
            await logout();
            navigate('/');
        } catch (error) {
            console.error('Logout error:', error);
        }
    };

    const toggleMobileDrawer = (open: boolean) => () => {
        setMobileDrawerOpen(open);
    };

    const toggleProfileDrawer = (open: boolean) => () => {
        setProfileDrawerOpen(open);
    };

    const navLinksDesktop = [
        { label: 'Find Events', path: '/events', action: () => navigate('/events') },
        { label: 'Upcoming Events', path: '/upcoming-events', action: () => navigate('/upcoming-events') },
        { label: 'Create Event', path: '/create-event', action: () => navigate('/create-event') },
    ];

    const navLinksMobile = [
        { label: 'Find Events', path: '/events', action: () => navigate('/events') },
        { label: 'Upcoming Events', path: '/upcoming-events', action: () => navigate('/upcoming-events') },
        { label: 'Create Event', path: '/create-event', action: () => navigate('/create-event') },
        { label: 'Team', path: '/team', action: () => navigate('/team') },
        { label: 'About', path: '/about', action: () => navigate('/about') },
        { label: 'Contact', path: '/contact', action: () => navigate('/contact') },
        { label: 'FAQs', path: '/faq', action: () => navigate('/faq') },
        { label: 'Help', path: '/help', action: () => navigate('/help') }
    ];

    const profileMenuItems = [
        ...(isAuthenticated ? [
            { label: 'My Account', path: '/account', action: () => navigate('/account') },
            { label: 'My Events', path: '/my-events', action: () => navigate('/my-events') },
            { label: 'My Bookings', path: '/my-bookings', action: () => navigate('/my-bookings') },
            { label: 'Logout', path: '/logout', action: handleLogout, isLogout: true },
        ] : [])
    ];

    const isActivePath = (path: string) => currentPath === path;

    return (
        <>
            <AppBar position="sticky" color="primary">
                <Toolbar sx={{ 
                    position: 'relative', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: isMobile ? 'space-between' : 'space-between',
                    gap: 1
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                        {isMobile && (
                            <IconButton 
                                color="inherit" 
                                edge="start" 
                                onClick={toggleMobileDrawer(true)}
                                sx={{ flexShrink: 0 }}
                            >
                                <MenuIcon />
                            </IconButton>
                        )}

                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1, 
                            flexGrow: isMobile ? 1 : 0 
                        }}>
                            <Box
                                component="img"
                                src="https://myeventshop-images.s3.eu-west-1.amazonaws.com/assets/mes-logo.png"
                                alt="MyEventShop"
                                sx={{
                                    height: { xs: '52.5px', sm: '50px', md: '58px' },
                                    cursor: 'pointer',
                                    objectFit: 'contain',
                                    zIndex: 2,
                                    display: 'flex',
                                    alignItems: 'center'
                                }}
                                onClick={() => navigate('/')}
                            />
                            {/* Beta Tag */}
                            <Box
                                sx={{
                                    bgcolor: '#FCD12A',
                                    color: 'black',
                                    fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.7rem' },
                                    fontWeight: 'bold',
                                    px: { xs: 0.5, sm: 0.6, md: 0.7 },
                                    py: { xs: 0.2, sm: 0.25, md: 0.3 },
                                    borderRadius: '8px',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                    flexShrink: 0
                                }}
                            >
                                Beta
                            </Box>
                        </Box>
                    </Box>

                    {!isMobile && (
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 2,
                            flexShrink: 0,
                            ml: 'auto',
                            mr: 2
                        }}>
                            {navLinksDesktop.map((nav) => (
                                <Button
                                    key={nav.label}
                                    color="inherit"
                                    onClick={nav.action}
                                    sx={{
                                        transition: 'color 0.2s ease',
                                        color: isActivePath(nav.path) ? '#FF9DE7' : 'inherit',
                                        fontWeight: isActivePath(nav.path) ? 'bold' : 'normal',
                                        '&:hover': {
                                            color: '#FCD12A'
                                        },
                                    }}
                                >
                                    {nav.label}
                                </Button>
                            ))}
                        </Box>
                    )}

                    {isMobile && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                            <IconButton 
                                color="inherit" 
                                onClick={() => setMobileSearchOpen(!mobileSearchOpen)}
                                sx={{ flexShrink: 0 }}
                            >
                                <SearchIcon />
                            </IconButton>
                            
                            <IconButton 
                                color="inherit" 
                                onClick={handleProfileMenuOpen}
                                sx={{ flexShrink: 0 }}
                            >
                                <AccountCircleIcon />
                            </IconButton>
                        </Box>
                    )}

                    {!isMobile && (
                        <IconButton 
                            color="inherit" 
                            onClick={handleProfileMenuOpen}
                            sx={{ flexShrink: 0 }}
                        >
                            <AccountCircleIcon />
                        </IconButton>
                    )}

                    {/* Centered Desktop SearchBar */}
                    {!isMobile && (
                        <Box sx={{ 
                            position: 'absolute', 
                            left: '50%', 
                            top: '50%',
                            transform: 'translate(-50%, -50%)', 
                            width: '40%',
                            zIndex: 1
                        }}>
                            <SearchBarWrapper />
                        </Box>
                    )}

                    {/* Profile Drawer */}
                    <Drawer
                        anchor="right"
                        open={profileDrawerOpen}
                        onClose={toggleProfileDrawer(false)}
                        PaperProps={{
                            sx: {
                                width: '100%',
                                maxWidth: '300px',
                                backgroundColor: '#4C2D57',
                                color: 'white',
                                height: '100vh',
                            },
                        }}
                        transitionDuration={300}
                    >
                        <Box sx={{ width: '100%', height: '100%', textAlign: 'center', p: 3 }}>
                            <IconButton
                                sx={{ position: 'absolute', top: 16, right: 16, color: 'white' }}
                                onClick={toggleProfileDrawer(false)}
                            >
                                <CloseIcon />
                            </IconButton>
                            <List sx={{ mt: 6 }}>
                        {!isAuthenticated ? (
                            <>
                                {onSignupClick ? (
                                            <ListItem
                                                component="button"
                                                onClick={() => {
                                                    onSignupClick();
                                                    toggleProfileDrawer(false)();
                                                }}
                                                sx={{
                                                    textAlign: 'center',
                                                    fontSize: '16px',
                                                    color: 'white',
                                                    '&:hover': { color: '#FF9DE7' },
                                                    textTransform: 'uppercase',
                                                    padding: '12px 0',
                                                    border: 'none',
                                                    display: 'block',
                                                    backgroundColor: 'transparent',
                                                }}
                                            >
                                                <ListItemText primary="Sign Up" sx={{ textAlign: 'center' }} />
                                            </ListItem>
                                        ) : (
                                            <ListItem
                                                component="button"
                                                onClick={() => {
                                                    navigate('/register', { state: { from: { pathname: currentPath } } });
                                                    toggleProfileDrawer(false)();
                                                }}
                                                sx={{
                                                    textAlign: 'center',
                                                    fontSize: '16px',
                                                    color: 'white',
                                                    '&:hover': { color: '#FF9DE7' },
                                                    textTransform: 'uppercase',
                                                    padding: '12px 0',
                                                    border: 'none',
                                                    display: 'block',
                                                    backgroundColor: 'transparent',
                                                }}
                                            >
                                                <ListItemText primary="Sign Up" sx={{ textAlign: 'center' }} />
                                            </ListItem>
                                )}
                                {onLoginClick ? (
                                            <ListItem
                                                component="button"
                                                onClick={() => {
                                                    onLoginClick();
                                                    toggleProfileDrawer(false)();
                                                }}
                                                sx={{
                                                    textAlign: 'center',
                                                    fontSize: '16px',
                                                    color: 'white',
                                                    '&:hover': { color: '#FF9DE7' },
                                                    textTransform: 'uppercase',
                                                    padding: '12px 0',
                                                    border: 'none',
                                                    display: 'block',
                                                    backgroundColor: 'transparent',
                                                }}
                                            >
                                                <ListItemText primary="Login" sx={{ textAlign: 'center' }} />
                                            </ListItem>
                                        ) : (
                                            <ListItem
                                                component="button"
                                                onClick={() => {
                                                    navigate('/login', { state: { from: { pathname: currentPath } } });
                                                    toggleProfileDrawer(false)();
                                                }}
                                                sx={{
                                                    textAlign: 'center',
                                                    fontSize: '16px',
                                                    color: 'white',
                                                    '&:hover': { color: '#FF9DE7' },
                                                    textTransform: 'uppercase',
                                                    padding: '12px 0',
                                                    border: 'none',
                                                    display: 'block',
                                                    backgroundColor: 'transparent',
                                                }}
                                            >
                                                <ListItemText primary="Login" sx={{ textAlign: 'center' }} />
                                            </ListItem>
                                        )}
                                    </>
                                ) : (
                                    profileMenuItems.map((item, index) => (
                                        <React.Fragment key={item.label}>
                                            <ListItem
                                                component="button"
                                                onClick={() => {
                                                    item.action();
                                                    toggleProfileDrawer(false)();
                                                }}
                                                sx={{
                                                    textAlign: 'center',
                                                    fontSize: '16px',
                                                    fontWeight: isActivePath(item.path) ? 'bold' : 'normal',
                                                    color: item.isLogout ? '#FF6B6B' : (isActivePath(item.path) ? '#FF9DE7' : 'white'),
                                                    '&:hover': { color: item.isLogout ? '#FF5252' : '#FF9DE7' },
                                                    textTransform: 'uppercase',
                                                    padding: '12px 0',
                                                    border: 'none',
                                                    borderBottom: index < profileMenuItems.length - 1 ? '1px solid rgba(255, 255, 255, 0.3)' : 'none',
                                                    display: 'block',
                                                    backgroundColor: 'transparent',
                                                }}
                                            >
                                                <ListItemText primary={item.label} sx={{ textAlign: 'center' }} />
                                            </ListItem>
                                        </React.Fragment>
                                    ))
                                )}
                            </List>
                        </Box>
                    </Drawer>

                    {/* Mobile Drawer */}
                    <Drawer
                        anchor="left"
                        open={mobileDrawerOpen}
                        onClose={toggleMobileDrawer(false)}
                        PaperProps={{
                            sx: {
                                width: '100%',
                                maxWidth: '300px',
                                backgroundColor: '#4C2D57',
                                color: 'white',
                                height: '100vh',
                            },
                        }}
                        transitionDuration={300}
                    >
                        <Box sx={{ width: '100%', height: '100%', textAlign: 'center', p: 3 }}>
                            <IconButton
                                sx={{ position: 'absolute', top: 16, left: 16, color: 'white' }}
                                onClick={toggleMobileDrawer(false)}
                            >
                                <CloseIcon />
                            </IconButton>
                            <List sx={{ mt: 6 }}>
                                {navLinksMobile.map((nav, index) => (
                                    <React.Fragment key={nav.label}>
                                        <ListItem
                                            component="button"
                                            onClick={() => {
                                                nav.action();
                                                toggleMobileDrawer(false)();
                                            }}
                                            sx={{
                                                textAlign: 'center',
                                                fontSize: '16px',
                                                fontWeight: isActivePath(nav.path) ? 'bold' : 'normal',
                                                color: isActivePath(nav.path) ? '#FF9DE7' : 'white',
                                                '&:hover': { color: '#FF9DE7' },
                                                textTransform: 'uppercase',
                                                padding: '12px 0',
                                                border: 'none',
                                                borderBottom: index < navLinksMobile.length - 1 ? '1px solid rgba(255, 255, 255, 0.3)' : 'none',
                                                display: 'block',
                                                backgroundColor: 'transparent',
                                            }}
                                        >
                                            <ListItemText primary={nav.label} sx={{ textAlign: 'center' }} />
                                        </ListItem>
                                    </React.Fragment>
                                ))}
                                
                                {/* Add Login/Signup options for mobile */}
                                {!isAuthenticated && (
                                    <>
                                        <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.3)', my: 1 }} />
                                        {onSignupClick ? (
                                            <ListItem
                                                component="button"
                                                onClick={() => {
                                                    onSignupClick();
                                                    toggleMobileDrawer(false)();
                                                }}
                                                sx={{
                                                    textAlign: 'center',
                                                    fontSize: '16px',
                                                    color: 'white',
                                                    '&:hover': { color: '#FF9DE7' },
                                                    textTransform: 'uppercase',
                                                    padding: '12px 0',
                                                    border: 'none',
                                                    display: 'block',
                                                    backgroundColor: 'transparent',
                                                }}
                                            >
                                                <ListItemText primary="Sign Up" sx={{ textAlign: 'center' }} />
                                            </ListItem>
                                        ) : (
                                            <ListItem
                                                component="button"
                                                onClick={() => {
                                                    navigate('/register');
                                                    toggleMobileDrawer(false)();
                                                }}
                                                sx={{
                                                    textAlign: 'center',
                                                    fontSize: '16px',
                                                    color: 'white',
                                                    '&:hover': { color: '#FF9DE7' },
                                                    textTransform: 'uppercase',
                                                    padding: '12px 0',
                                                    border: 'none',
                                                    display: 'block',
                                                    backgroundColor: 'transparent',
                                                }}
                                            >
                                                <ListItemText primary="Sign Up" sx={{ textAlign: 'center' }} />
                                            </ListItem>
                                        )}
                                        {onLoginClick ? (
                                            <ListItem
                                                component="button"
                                                onClick={() => {
                                                    onLoginClick();
                                                    toggleMobileDrawer(false)();
                                                }}
                                                sx={{
                                                    textAlign: 'center',
                                                    fontSize: '16px',
                                                    color: 'white',
                                                    '&:hover': { color: '#FF9DE7' },
                                                    textTransform: 'uppercase',
                                                    padding: '12px 0',
                                                    border: 'none',
                                                    display: 'block',
                                                    backgroundColor: 'transparent',
                                                }}
                                            >
                                                <ListItemText primary="Login" sx={{ textAlign: 'center' }} />
                                            </ListItem>
                                        ) : (
                                            <ListItem
                                                component="button"
                                                onClick={() => {
                                                    navigate('/login', { state: { from: { pathname: currentPath } } });
                                                    toggleMobileDrawer(false)();
                                                }}
                                                sx={{
                                                    textAlign: 'center',
                                                    fontSize: '16px',
                                                    color: 'white',
                                                    '&:hover': { color: '#FF9DE7' },
                                                    textTransform: 'uppercase',
                                                    padding: '12px 0',
                                                    border: 'none',
                                                    display: 'block',
                                                    backgroundColor: 'transparent',
                                                }}
                                            >
                                                <ListItemText primary="Login" sx={{ textAlign: 'center' }} />
                                            </ListItem>
                                        )}
                                    </>
                                )}
                            </List>
                        </Box>
                    </Drawer>
                </Toolbar>
            </AppBar>

            {/* Animated Mobile Search */}
            {isMobile && (
                <Collapse in={mobileSearchOpen}>
                    <Box sx={{ px: 2, pt: 0, backgroundColor: '#5F3C6F' }}>
                        <SearchBarWrapper />
                    </Box>
                </Collapse>
            )}
        </>
    );
};

export default AppMenu;
